// Slot Availability Calendar (verticals service) — the single adapter module
// the widget talks to. Every read/write from every phase of the plan goes
// through here; nothing else in the app should import ServiceEndpoints'
// SLOT_CALENDAR_* constants directly. See
// docs/plans/slot-availability-calendar-plan.md for the full endpoint table
// this mirrors (section 3) and the concurrency protocol (section 4).
//
// Envelope: the backend doc states every verticals response is a classic
// NineBusinessRs<T> (errorInformation / generalProcessingStatus /
// List<T> contents), with "a singleton payload occupies one contents
// element; a paged response has one page object in contents" (section 2) —
// i.e. contents is ALWAYS array-wrapped, singleton or paged alike. So every
// function below follows the exact same
// unwrapEnvelope -> coerceSingle -> convert pipeline, with no per-endpoint
// envelope special-casing needed.
//
// Only PUT/GET of the calendar and GET of its change history are confirmed
// deployed as of this writing (flag AppConfig.featureFlags.enableSlotCalendar
// is off) — every other function here is written against the documented
// contract but UNVERIFIED against a live response. Flagged inline wherever
// that matters for a specific field/shape (also see SlotCalendarObject.ts's
// header comment).

import { ServiceEndpoints } from "../../constants/ServiceEndpoints";
import { getRequest, postRequest, putRequest } from "../http/httpRequest";
import { ApiEnvelopeError, coerceSingle, unwrapEnvelope } from "../helpers/ApiEnvelopeHelper";
import {
  buildIfMatchHeaders,
  buildIfNoneMatchCreateHeaders,
  parseCalendarEtag,
} from "../helpers/SlotCalendarMutationHelper";
import SlotCalendarConvertor from "../converters/uiToBackend/SlotCalendarConvertor";
import {
  convertSlotAvailabilityPage,
  convertSlotBlock,
  convertSlotBlockPreview,
  convertSlotBookingEntry,
  convertSlotCalendarChange,
  convertSlotCalendarMeta,
  convertSlotCalendarMutationResult,
  convertSlotCalendarPage,
  convertSlotCalendarPreview,
  convertSlotCalendarSummary,
  convertSlotOwnerAvailabilityPage,
  convertSlotOwnerAvailabilitySlot,
  convertSlotScheduleVersion,
} from "../converters/backendToUi/slotCalendarConvertor";
import {
  CreateAvailabilitySlotInput,
  CreateSlotBlockInput,
  CreateSlotBookingEntryInput,
  CreateSlotScheduleVersionInput,
  PreviewSlotCalendarInput,
  PreviewSlotScheduleInput,
  SlotAvailabilityPage,
  SlotBlock,
  SlotBlockPreview,
  SlotBookingEntry,
  SlotCalendarChange,
  SlotCalendarMeta,
  SlotCalendarMutationResult,
  SlotCalendarPage,
  SlotCalendarPreview,
  SlotCalendarSummary,
  SlotCalendarWriteInput,
  SlotOwnerAvailabilityPage,
  SlotOwnerAvailabilitySlot,
  SlotScheduleVersion,
  SlotSummaryBucket,
  UpdateAvailabilitySlotInput,
  UpdateSlotBookingEntryInput,
} from "../../interfaces/SlotCalendarObject";

const BEARER = true;
const PUBLIC = false;

function calendarBase(productId: string): string {
  return `${ServiceEndpoints.SLOT_CALENDAR_PRODUCT_BASE}/${productId}/slot-calendar`;
}

// ─── Error codes & friendly copy ─────────────────────────────────────────────
// Same pattern as DineInApiService's DINE_IN_ERROR_CODES — defined next to the
// service that produces them, not in a shared enum file, since these are this
// one controller family's own vocabulary.

export const SLOT_CALENDAR_ERROR_CODES = {
  INVALID_REQUEST: "14301",
  UNAUTHENTICATED: "14302",
  NOT_EFFECTIVE_OWNER: "14303",
  PRODUCT_NOT_FOUND: "14304",
  BUSINESS_NOT_FOUND: "14305",
  BUSINESS_REQUIRED: "14306",
  RENTAL_LISTING_REQUIRED: "14307",
  CALENDAR_NOT_FOUND: "14308",
  PRECONDITION_REQUIRED: "14309",
  PRECONDITION_FAILED: "14310",
  IDEMPOTENCY_KEY_REUSED: "14311",
  CURSOR_STALE: "14312",
  PUBLICATION_NOT_READY: "14313",
  DEPENDENCY_UNAVAILABLE: "14314",
  PAYLOAD_TOO_LARGE: "14315",
  INVALID_SCHEDULE: "14316",
  RANGE_TOO_COMPLEX: "14317",
  SLOT_NOT_FOUND: "14318",
  SLOT_OVERLAP: "14319",
  OCCURRENCE_CHANGED: "14320",
  CAPACITY_EXCEEDED: "14321",
  CAPACITY_BELOW_OCCUPANCY: "14322",
  SLOT_NOT_OPEN: "14323",
  SLOT_PAST: "14324",
  BLOCK_AFFECTS_BOOKINGS: "14325",
  PRODUCT_HAS_CALENDAR_HISTORY: "14326",
  BOOKING_ENTRY_NOT_FOUND: "14327",
  BOOKING_ENTRY_ALREADY_EXISTS: "14328",
  INVALID_STATE_TRANSITION: "14329",
  INTERNAL_ERROR: "14330",
  SCHEDULE_VERSION_NOT_FOUND: "14331",
} as const;

const C = SLOT_CALENDAR_ERROR_CODES;

/** Codes whose server `errorDetail` names the exact problem (handover doc:
 *  "show errorDetail") - surfaced verbatim instead of generic copy. */
const DETAIL_FIRST_CODES = new Set<string>([
  C.INVALID_REQUEST,
  C.PUBLICATION_NOT_READY,
  C.INVALID_SCHEDULE,
  C.INVALID_STATE_TRANSITION,
]);

const FRIENDLY_MESSAGES: Record<string, string> = {
  [C.UNAUTHENTICATED]: "Please sign in again to continue.",
  [C.NOT_EFFECTIVE_OWNER]: "Only the business owner can manage this listing's availability.",
  [C.PRODUCT_NOT_FOUND]: "This listing no longer exists.",
  [C.BUSINESS_NOT_FOUND]: "This listing's business couldn't be found.",
  [C.BUSINESS_REQUIRED]: "Only listings attached to a business can have availability.",
  [C.RENTAL_LISTING_REQUIRED]: "Availability is only available for rental listings.",
  [C.CALENDAR_NOT_FOUND]: "Availability isn't set up for this listing yet.",
  [C.PRECONDITION_REQUIRED]: "Something went wrong preparing that request. Please try again.",
  [C.PRECONDITION_FAILED]: "This calendar changed since you loaded it. Please review and try again.",
  [C.IDEMPOTENCY_KEY_REUSED]: "That action was already submitted with different details. Please try again.",
  [C.CURSOR_STALE]: "This list changed while loading. Please try again.",
  [C.DEPENDENCY_UNAVAILABLE]: "The service is busy right now. Please try again in a moment.",
  [C.PAYLOAD_TOO_LARGE]: "That's too much information for one request - shorten the notes or labels.",
  [C.RANGE_TOO_COMPLEX]: "That range is too large to check - try a shorter range.",
  [C.SLOT_NOT_FOUND]: "That slot isn't available anymore.",
  [C.SLOT_OVERLAP]: "That overlaps with existing slots.",
  [C.OCCURRENCE_CHANGED]: "This slot changed since you loaded it. It's been refreshed - please try again.",
  [C.CAPACITY_EXCEEDED]: "Not enough spots left for that.",
  [C.CAPACITY_BELOW_OCCUPANCY]: "Capacity can't go below what's already booked.",
  [C.SLOT_NOT_OPEN]: "This slot is closed or under maintenance.",
  [C.SLOT_PAST]: "That time has already passed.",
  [C.BLOCK_AFFECTS_BOOKINGS]: "That range has bookings. Confirm to block it anyway.",
  [C.PRODUCT_HAS_CALENDAR_HISTORY]: "This listing has availability history and can't be deleted - unpublish it instead.",
  [C.BOOKING_ENTRY_NOT_FOUND]: "That booking isn't there anymore.",
  [C.BOOKING_ENTRY_ALREADY_EXISTS]: "That booking was already saved.",
  [C.INTERNAL_ERROR]: "Something went wrong on our side. Please try again.",
  [C.SCHEDULE_VERSION_NOT_FOUND]: "That schedule isn't there anymore. Please refresh.",
  // Framework-level failures that never reach the slot-calendar handler
  // carry no errorCode - unwrapEnvelope falls back to HTTP_<status>.
  HTTP_412: "This calendar changed since you loaded it. Please review and try again.",
  HTTP_428: "Something went wrong preparing that request. Please try again.",
  HTTP_413: "That's too much information for one request.",
};

export function hasSlotCalendarErrorCode(error: unknown, code: string): boolean {
  return error instanceof ApiEnvelopeError && error.errorCode === code;
}

export function toFriendlySlotCalendarError(error: unknown): string {
  if (error instanceof ApiEnvelopeError) {
    if (DETAIL_FIRST_CODES.has(error.errorCode) && error.message) return error.message;
    return FRIENDLY_MESSAGES[error.errorCode] ?? error.message;
  }
  return error instanceof Error ? error.message : "Something went wrong. Please try again.";
}

/** True for a 412 (stale If-Match) — the one conflict every mutation caller
 *  should react to the same way: refetch calendar meta and let the user
 *  retry, rather than just toasting an error. */
export function isStaleRevisionError(error: unknown): boolean {
  return (
    error instanceof ApiEnvelopeError &&
    (error.errorCode === C.PRECONDITION_FAILED || error.errorCode === "HTTP_412")
  );
}

/** True for CALENDAR_NOT_FOUND — the owner "not set up yet" state, not a
 *  failure worth surfacing as an error toast. */
export function isCalendarNotFoundError(error: unknown): boolean {
  return hasSlotCalendarErrorCode(error, C.CALENDAR_NOT_FOUND) || hasSlotCalendarErrorCode(error, SPA_FALLBACK_NOT_FOUND);
}

// ─── Internal request helpers ────────────────────────────────────────────────

/** Synthetic code for CloudFront's HTML fallback (see unwrap). Counts as
 *  CALENDAR_NOT_FOUND everywhere a "no calendar" 404 is expected. */
const SPA_FALLBACK_NOT_FOUND = "SPA_FALLBACK_NOT_FOUND";

function isSpaFallbackResponse(response: Response): boolean {
  return (response.headers.get("Content-Type") ?? "").toLowerCase().includes("text/html");
}

/** unwrapEnvelope + dev-only failure log of the four things the handover doc
 *  says to capture: errorCode, errorDetail, request body, sent headers (never
 *  Authorization - httpRequest adds that itself, it's not in `headers`). */
async function unwrap(response: Response, payload?: unknown, headers?: Record<string, string>): Promise<unknown> {
  // TEMPORARY (remove once CloudFront E1RW7GH616CO53 stops mapping 403/404 to
  // /index.html for /api/* too): the API never returns HTML, so an HTML body
  // here is CloudFront's SPA fallback standing in for a backend 404.
  if (isSpaFallbackResponse(response)) {
    if (import.meta.env.DEV) {
      console.warn("[SlotCalendar] got CloudFront's index.html instead of an API response - treating as 404", {
        url: response.url,
        status: response.status,
      });
    }
    throw new ApiEnvelopeError(SPA_FALLBACK_NOT_FOUND, "Not found");
  }
  try {
    return await unwrapEnvelope<unknown>(response);
  } catch (error) {
    if (import.meta.env.DEV && error instanceof ApiEnvelopeError) {
      console.warn("[SlotCalendar] request failed", {
        url: response.url,
        status: response.status,
        errorCode: error.errorCode,
        errorDetail: error.message,
        additionalDetails: error.additionalDetails,
        body: payload,
        headers,
      });
    }
    throw error;
  }
}

async function ownerGet<T>(endpoint: string): Promise<{ contents: unknown; etag: string | null }> {
  const response = await getRequest(endpoint, BEARER);
  const etag = parseCalendarEtag(response);
  const contents = coerceSingle(await unwrap(response));
  return { contents, etag };
}

async function publicGet(endpoint: string): Promise<unknown> {
  const response = await getRequest(endpoint, PUBLIC);
  return coerceSingle(await unwrap(response));
}

interface MutationOpts {
  idempotencyKey: string;
  /** Omit only for the one write with no prior revision — creating the
   *  calendar itself (If-None-Match: * instead of If-Match). */
  ifMatch?: string;
}

function mutationHeaders(opts: MutationOpts): Record<string, string> {
  return opts.ifMatch
    ? buildIfMatchHeaders(opts.ifMatch, opts.idempotencyKey)
    : buildIfNoneMatchCreateHeaders(opts.idempotencyKey);
}

async function ownerPost(
  endpoint: string,
  payload: unknown,
  opts: MutationOpts
): Promise<{ contents: unknown; etag: string | null }> {
  const headers = mutationHeaders(opts);
  const response = await postRequest(endpoint, payload, BEARER, headers);
  const etag = parseCalendarEtag(response);
  const contents = coerceSingle(await unwrap(response, payload, headers));
  return { contents, etag };
}

async function ownerPut(
  endpoint: string,
  payload: unknown,
  opts: MutationOpts
): Promise<{ contents: unknown; etag: string | null }> {
  const headers = mutationHeaders(opts);
  const response = await putRequest(endpoint, payload, BEARER, headers);
  const etag = parseCalendarEtag(response);
  const contents = coerceSingle(await unwrap(response, payload, headers));
  return { contents, etag };
}

/** POST used as a read-only dry run (previews) — still owner-authenticated,
 *  never touches If-Match/Idempotency-Key since it can't mutate anything. */
async function ownerPreviewPost(endpoint: string, payload: unknown): Promise<unknown> {
  const response = await postRequest(endpoint, payload, BEARER);
  return coerceSingle(await unwrap(response, payload));
}

function query(params: Record<string, string | number | boolean | undefined>): string {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined) search.set(key, String(value));
  });
  const qs = search.toString();
  return qs ? `?${qs}` : "";
}

// ─── Calendar ────────────────────────────────────────────────────────────────

/** POST /slot-calendar-previews — no product needed yet, usable while
 *  authoring a schedule before the listing itself is created. */
async function previewCalendar(input: PreviewSlotCalendarInput): Promise<SlotCalendarPreview> {
  const payload = SlotCalendarConvertor.buildPreviewCalendarPayload(input);
  const contents = await ownerPreviewPost(ServiceEndpoints.SLOT_CALENDAR_PREVIEWS, payload);
  return convertSlotCalendarPreview(contents);
}

/** PUT B — creates the calendar (opts.ifMatch omitted) or updates
 *  publication (opts.ifMatch = current etag; timezone must repeat unchanged). */
async function putSlotCalendar(
  productId: string,
  input: SlotCalendarWriteInput,
  opts: MutationOpts
): Promise<{ result: SlotCalendarMutationResult; etag: string | null }> {
  const payload = SlotCalendarConvertor.buildSlotCalendarPayload(input);
  const { contents, etag } = await ownerPut(calendarBase(productId), payload, opts);
  return { result: convertSlotCalendarMutationResult(contents), etag };
}

/** GET B (owner). Confirmed deployed. */
async function getSlotCalendar(productId: string): Promise<{ meta: SlotCalendarMeta; etag: string | null }> {
  const { contents, etag } = await ownerGet(calendarBase(productId));
  return { meta: convertSlotCalendarMeta(contents), etag };
}

/** GET B, but "no calendar yet" (14308) is null instead of an error - the
 *  read every PUT B starts with, so create-vs-update is decided by what the
 *  SERVER has, never by UI state (backend 412 handover). */
async function findSlotCalendar(productId: string): Promise<{ meta: SlotCalendarMeta; etag: string | null } | null> {
  try {
    return await getSlotCalendar(productId);
  } catch (error) {
    if (isCalendarNotFoundError(error)) return null;
    throw error;
  }
}

// ─── Schedule versions ───────────────────────────────────────────────────────

async function previewSchedule(productId: string, input: PreviewSlotScheduleInput): Promise<SlotCalendarPreview> {
  const payload = SlotCalendarConvertor.buildPreviewSchedulePayload(input);
  const contents = await ownerPreviewPost(`${calendarBase(productId)}/schedule-previews`, payload);
  return convertSlotCalendarPreview(contents);
}

async function createScheduleVersion(
  productId: string,
  input: CreateSlotScheduleVersionInput,
  opts: MutationOpts
): Promise<{ result: SlotCalendarMutationResult; etag: string | null }> {
  const payload = SlotCalendarConvertor.buildCreateScheduleVersionPayload(input);
  const { contents, etag } = await ownerPost(`${calendarBase(productId)}/schedule-versions`, payload, opts);
  return { result: convertSlotCalendarMutationResult(contents), etag };
}

export interface CursorPageInput {
  cursor?: string;
  limit?: number;
}

/** Pagination shape unconfirmed — see SlotCalendarPage's own doc comment. */
async function getScheduleVersions(
  productId: string,
  input: CursorPageInput = {}
): Promise<SlotCalendarPage<SlotScheduleVersion>> {
  const endpoint = `${calendarBase(productId)}/schedule-versions${query({ cursor: input.cursor, limit: input.limit })}`;
  const { contents } = await ownerGet(endpoint);
  return convertSlotCalendarPage(contents, convertSlotScheduleVersion);
}

async function getScheduleVersion(productId: string, versionId: string): Promise<SlotScheduleVersion> {
  const { contents } = await ownerGet(`${calendarBase(productId)}/schedule-versions/${versionId}`);
  return convertSlotScheduleVersion(contents);
}

// ─── Availability (read) ─────────────────────────────────────────────────────

export interface InstantRangeInput extends CursorPageInput {
  /** ISO-8601 instant with offset. */
  fromIso: string;
  /** ISO-8601 instant with offset, exclusive. */
  toIsoExclusive: string;
}

export interface DateRangeInput {
  fromDate: string;
  toDateExclusive: string;
  bucket: SlotSummaryBucket;
}

/** GET B/availability — public, visibility-checked. At most 31 local days
 *  per request (page size default 200, max 500) per the backend doc. */
async function getPublicAvailability(
  productId: string,
  input: InstantRangeInput
): Promise<SlotAvailabilityPage> {
  const endpoint = `${calendarBase(productId)}/availability${query({
    from: input.fromIso,
    to: input.toIsoExclusive,
    cursor: input.cursor,
    limit: input.limit,
  })}`;
  const response = await getRequest(endpoint, PUBLIC);
  const contents = coerceSingle(await unwrap(response));
  return convertSlotAvailabilityPage(contents);
}

/** GET B/availability-summary — public, visibility-checked. DAY buckets: max
 *  62 days; MONTH buckets: max 12 months. */
async function getPublicAvailabilitySummary(
  productId: string,
  input: DateRangeInput
): Promise<SlotCalendarSummary> {
  const endpoint = `${calendarBase(productId)}/availability-summary${query({
    fromDate: input.fromDate,
    toDateExclusive: input.toDateExclusive,
    bucket: input.bucket,
  })}`;
  const response = await getRequest(endpoint, PUBLIC);
  const contents = coerceSingle(await unwrap(response));
  return convertSlotCalendarSummary(contents);
}

// ─── Paging ─────────────────────────────────────────────────────────────────

interface CursorPage<T> {
  items: T[];
  nextCursor: string | null;
  hasMore: boolean;
}

/** Hard stop so a misbehaving cursor can never loop forever (31 days of
 *  5-minute slots is ~9k items = 18 pages at limit 500). */
const MAX_PAGES = 40;

/** Follows nextCursor until hasMore is false, keeping from/to/limit fixed
 *  (changing them invalidates the cursor). A stale cursor (14312 - the
 *  calendar changed mid-paging) or a rejected one restarts ONCE from page
 *  one, per the handover doc. Returns the last page's metadata with every
 *  page's items merged. */
async function fetchAllPages<P extends CursorPage<unknown>>(fetchPage: (cursor?: string) => Promise<P>): Promise<P> {
  for (let attempt = 0; attempt < 2; attempt++) {
    try {
      let page = await fetchPage(undefined);
      const items: P["items"] = [...page.items];
      for (let i = 1; page.hasMore && page.nextCursor && i < MAX_PAGES; i++) {
        page = await fetchPage(page.nextCursor);
        items.push(...page.items);
      }
      return { ...page, items, hasMore: false, nextCursor: null } as P;
    } catch (error) {
      const cursorRejected =
        hasSlotCalendarErrorCode(error, C.CURSOR_STALE) || hasSlotCalendarErrorCode(error, C.INVALID_REQUEST);
      if (attempt === 0 && cursorRejected) continue;
      throw error;
    }
  }
  throw new Error("Couldn't load availability. Please try again.");
}

/** Every page of public availability for one range. */
async function getAllPublicAvailability(productId: string, input: InstantRangeInput): Promise<SlotAvailabilityPage> {
  return fetchAllPages((cursor) => getPublicAvailability(productId, { ...input, cursor }));
}

/** Every page of owner availability for one range. */
async function getAllOwnerAvailability(
  productId: string,
  input: InstantRangeInput & { includeArchived?: boolean }
): Promise<SlotOwnerAvailabilityPage> {
  return fetchAllPages((cursor) => getOwnerAvailability(productId, { ...input, cursor }));
}

/** GET B/slots — owner mirror of getPublicAvailability, same range rules,
 *  plus includeArchived and owner-only fields on each item. */
async function getOwnerAvailability(
  productId: string,
  input: InstantRangeInput & { includeArchived?: boolean }
): Promise<SlotOwnerAvailabilityPage> {
  const endpoint = `${calendarBase(productId)}/slots${query({
    from: input.fromIso,
    to: input.toIsoExclusive,
    cursor: input.cursor,
    limit: input.limit,
    includeArchived: input.includeArchived,
  })}`;
  const { contents } = await ownerGet(endpoint);
  return convertSlotOwnerAvailabilityPage(contents);
}

/** GET B/summary — owner mirror of getPublicAvailabilitySummary. */
async function getOwnerSummary(
  productId: string,
  input: DateRangeInput & { includeArchived?: boolean }
): Promise<SlotCalendarSummary> {
  const endpoint = `${calendarBase(productId)}/summary${query({
    fromDate: input.fromDate,
    toDateExclusive: input.toDateExclusive,
    bucket: input.bucket,
    includeArchived: input.includeArchived,
  })}`;
  const { contents } = await ownerGet(endpoint);
  return convertSlotCalendarSummary(contents);
}

// ─── Manual / individual slots ───────────────────────────────────────────────

async function createAvailabilitySlot(
  productId: string,
  input: CreateAvailabilitySlotInput,
  opts: MutationOpts
): Promise<{ result: SlotCalendarMutationResult; etag: string | null }> {
  const payload = SlotCalendarConvertor.buildCreateAvailabilitySlotPayload(input);
  const { contents, etag } = await ownerPost(`${calendarBase(productId)}/slots`, payload, opts);
  return { result: convertSlotCalendarMutationResult(contents), etag };
}

/** GET B/slots/{slotId} — pass `occurrenceStart` whenever the slot might
 *  still be virtual (not yet materialized), per the backend doc's locator
 *  rules. */
async function getOwnerSlotDetail(
  productId: string,
  slotId: string,
  occurrenceStart?: string
): Promise<SlotOwnerAvailabilitySlot> {
  const endpoint = `${calendarBase(productId)}/slots/${slotId}${query({ occurrenceStart })}`;
  const { contents } = await ownerGet(endpoint);
  return convertSlotOwnerAvailabilitySlot(contents);
}

async function updateAvailabilitySlot(
  productId: string,
  slotId: string,
  input: UpdateAvailabilitySlotInput,
  opts: MutationOpts
): Promise<{ result: SlotCalendarMutationResult; etag: string | null }> {
  const payload = SlotCalendarConvertor.buildUpdateAvailabilitySlotPayload(input);
  const { contents, etag } = await ownerPut(`${calendarBase(productId)}/slots/${slotId}`, payload, opts);
  return { result: convertSlotCalendarMutationResult(contents), etag };
}

// ─── Blocks (range status events) ───────────────────────────────────────────

async function previewBlock(productId: string, input: CreateSlotBlockInput): Promise<SlotBlockPreview> {
  const payload = SlotCalendarConvertor.buildCreateBlockPayload(input);
  const contents = await ownerPreviewPost(`${calendarBase(productId)}/block-previews`, payload);
  return convertSlotBlockPreview(contents);
}

async function createBlock(
  productId: string,
  input: CreateSlotBlockInput,
  opts: MutationOpts
): Promise<{ result: SlotCalendarMutationResult; etag: string | null }> {
  const payload = SlotCalendarConvertor.buildCreateBlockPayload(input);
  const { contents, etag } = await ownerPost(`${calendarBase(productId)}/blocks`, payload, opts);
  return { result: convertSlotCalendarMutationResult(contents), etag };
}

/** Pagination shape unconfirmed — see SlotCalendarPage's own doc comment. */
async function getBlocks(productId: string, input: InstantRangeInput): Promise<SlotCalendarPage<SlotBlock>> {
  const endpoint = `${calendarBase(productId)}/blocks${query({
    from: input.fromIso,
    to: input.toIsoExclusive,
    cursor: input.cursor,
    limit: input.limit,
  })}`;
  const { contents } = await ownerGet(endpoint);
  return convertSlotCalendarPage(contents, convertSlotBlock);
}

// ─── Booking entries ─────────────────────────────────────────────────────────

async function createBookingEntry(
  productId: string,
  slotId: string,
  input: CreateSlotBookingEntryInput,
  opts: MutationOpts
): Promise<{ result: SlotCalendarMutationResult; etag: string | null }> {
  const payload = SlotCalendarConvertor.buildCreateBookingEntryPayload(input);
  const { contents, etag } = await ownerPost(
    `${calendarBase(productId)}/slots/${slotId}/booking-entries`,
    payload,
    opts
  );
  return { result: convertSlotCalendarMutationResult(contents), etag };
}

export interface GetBookingEntriesInput extends CursorPageInput {
  /** Required whenever the slot might be virtual — same locator rule as
   *  getOwnerSlotDetail. */
  occurrenceStart?: string;
  status?: "ACTIVE" | "CANCELLED";
}

/** Pagination shape unconfirmed — see SlotCalendarPage's own doc comment. */
async function getBookingEntries(
  productId: string,
  slotId: string,
  input: GetBookingEntriesInput = {}
): Promise<SlotCalendarPage<SlotBookingEntry>> {
  const endpoint = `${calendarBase(productId)}/slots/${slotId}/booking-entries${query({
    occurrenceStart: input.occurrenceStart,
    status: input.status,
    cursor: input.cursor,
    limit: input.limit,
  })}`;
  const { contents } = await ownerGet(endpoint);
  return convertSlotCalendarPage(contents, convertSlotBookingEntry);
}

async function getBookingEntry(productId: string, slotId: string, entryId: string): Promise<SlotBookingEntry> {
  const { contents } = await ownerGet(`${calendarBase(productId)}/slots/${slotId}/booking-entries/${entryId}`);
  return convertSlotBookingEntry(contents);
}

async function updateBookingEntry(
  productId: string,
  slotId: string,
  entryId: string,
  input: UpdateSlotBookingEntryInput,
  opts: MutationOpts
): Promise<{ result: SlotCalendarMutationResult; etag: string | null }> {
  const payload = SlotCalendarConvertor.buildUpdateBookingEntryPayload(input);
  const { contents, etag } = await ownerPut(
    `${calendarBase(productId)}/slots/${slotId}/booking-entries/${entryId}`,
    payload,
    opts
  );
  return { result: convertSlotCalendarMutationResult(contents), etag };
}

// ─── Change history ──────────────────────────────────────────────────────────

export interface GetChangesInput extends CursorPageInput {
  sinceRevision?: number;
}

/** GET B/changes. Confirmed deployed. Default page size 50, max 200. */
async function getChanges(productId: string, input: GetChangesInput = {}): Promise<SlotCalendarPage<SlotCalendarChange>> {
  const endpoint = `${calendarBase(productId)}/changes${query({
    sinceRevision: input.sinceRevision,
    cursor: input.cursor,
    limit: input.limit,
  })}`;
  const { contents } = await ownerGet(endpoint);
  return convertSlotCalendarPage(contents, convertSlotCalendarChange);
}

const SlotCalendarApiService = {
  previewCalendar,
  putSlotCalendar,
  getSlotCalendar,
  findSlotCalendar,
  previewSchedule,
  createScheduleVersion,
  getScheduleVersions,
  getScheduleVersion,
  getPublicAvailability,
  getPublicAvailabilitySummary,
  getOwnerAvailability,
  getOwnerSummary,
  createAvailabilitySlot,
  getOwnerSlotDetail,
  updateAvailabilitySlot,
  getAllPublicAvailability,
  getAllOwnerAvailability,
  previewBlock,
  createBlock,
  getBlocks,
  createBookingEntry,
  getBookingEntries,
  getBookingEntry,
  updateBookingEntry,
  getChanges,
};

export default SlotCalendarApiService;
